import { MoreVert } from '@mui/icons-material';
import { Box, Checkbox, FormControlLabel, IconButton, MenuItem, Popover, Typography } from '@mui/material';
import React, { useState } from 'react';
import ImportAndVerifyContainer from '../import/ImportAndVerifyContainer';
import usePermissionAllowed from '../../hooks/usePermissionAllowed';
import { Actions } from '../../services/react-query/permissions';
import { ITableConfigurableModules } from '../../types/Itable';
import { UserConfigurableModule } from '../../types/User';
import { Loading } from '../../uikit/loading/Loading';
import { ColumnSelectorOption } from '../../uikit/itable/ITable';

export interface MoreListActionsProps {
  module: ITableConfigurableModules;
  availableColumns: ColumnSelectorOption[];
  selectedKeys: string[];
  isDownloadingCsv?: boolean;
  onChange: (keys: string[]) => void;
  onDownloadCsv?: () => void;
}

export default function MoreListActions({ module, availableColumns, selectedKeys, isDownloadingCsv, onChange, onDownloadCsv }: MoreListActionsProps) {
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [columnsAnchor, setColumnsAnchor] = useState<HTMLElement | null>(null);
  const [importOpen, setImportOpen] = useState(false);

  const canDownloadCsv = usePermissionAllowed(Actions.REPORT_READ);
  const canImport = [UserConfigurableModule.ACCOUNTS, UserConfigurableModule.EMPLOYEES, UserConfigurableModule.CONTACTS].includes(module);

  return (
    <>
      <IconButton onClick={(e) => setMenuAnchor(e.currentTarget)} sx={{ p: 0 }}>
        <MoreVert />
      </IconButton>

      <Popover open={Boolean(menuAnchor)} anchorEl={menuAnchor} onClose={() => setMenuAnchor(null)}>
        <Box p={1}>
          <MenuItem
            onClick={() => {
              setColumnsAnchor(menuAnchor);
              setMenuAnchor(null);
            }}
          >
            Set Columns
          </MenuItem>

          {canImport && (
            <MenuItem
              onClick={() => {
                setMenuAnchor(null);
                setImportOpen(true);
              }}
            >
              Import
            </MenuItem>
          )}

          <MenuItem
            disabled={!(onDownloadCsv && canDownloadCsv)}
            onClick={() => {
              onDownloadCsv?.();
              setMenuAnchor(null);
            }}
          >
            {isDownloadingCsv ? (
              <>
                Downloading...
                <Loading size={12} />
              </>
            ) : (
              'Download CSV'
            )}
          </MenuItem>
        </Box>
      </Popover>

      <Popover open={Boolean(columnsAnchor)} anchorEl={columnsAnchor} onClose={() => setColumnsAnchor(null)}>
        <Box p={2} display="flex" flexDirection="column">
          <Typography variant="h6">Columns</Typography>

          {availableColumns.map((column) => (
            <FormControlLabel
              key={column.key}
              control={
                <Checkbox
                  checked={selectedKeys.includes(column.key)}
                  onChange={(e) => onChange(e.target.checked ? [...selectedKeys, column.key] : selectedKeys.filter((k) => k !== column.key))}
                />
              }
              label={column.label}
            />
          ))}
        </Box>
      </Popover>

      {canImport && (
        <ImportAndVerifyContainer open={importOpen} onCloseRequest={() => setImportOpen(false)} module={module} templateUrl={`/import-sheets/${module}.xlsx`} />
      )}
    </>
  );
}
